import { computed, ref, shallowRef } from 'vue';
import {
  HEARTBEAT_INTERVAL_MS,
  RECONNECT_BASE_DELAY_MS,
  RECONNECT_MAX_DELAY_MS,
  WS_PATH,
  WS_PROTOCOL_VERSION,
  type ClientMessage,
  type PresenceUser,
  type ServerMessage,
  type ActivitySelection,
  type ActivityContext,
  type ClientObservationMessage,
} from '@yana/shared';
import { api, isOwnOperation, notifyUnauthenticated } from '@/api/client';
import { clearSheetOperations, ownSheetOperation } from '@/api/sheet-operations';
import { useAppearanceStore } from '@/stores/appearance';
import { useAuthStore } from '@/stores/auth';
import { useDictionariesStore } from '@/stores/dictionaries';
import { useGridStore } from '@/stores/grid';
import { useSheetsStore } from '@/stores/sheets';
import { useToastStore } from '@/stores/toast';

/**
 * Совместная работа в реальном времени.
 *
 * Модуль-одиночка: соединение одно на вкладку, сколько бы компонентов
 * к нему ни обращалось. Отваливается — работа продолжается через REST,
 * пользователь видит баннер «нет синхронизации», а не белый экран.
 */

type ConnectionState = 'connecting' | 'online' | 'offline';

const state = ref<ConnectionState>('offline');
const presence = shallowRef<PresenceUser[]>([]);
const lastSeq = ref(0);
const myUserId = ref<string | null>(null);

let socket: WebSocket | null = null;
let reconnectAttempt = 0;
let reconnectTimer: number | null = null;
let heartbeatTimer: number | null = null;
let connectionTimeoutTimer: number | null = null;
let started = false;
const consumers = new Set<symbol>();
const listeners = new Set<(message: ServerMessage) => void>();
let subscribedRows: string[] = [];
let activityContext: ActivityContext | undefined;
let currentSelection: ActivitySelection | null = null;
let selectionTimer: number | null = null;
let selectionKey = '';

/** Кто какую ячейку правит прямо сейчас: ключ «rowId:field». */
const remoteEditing = ref<Record<string, { name: string; color: string }>>({});

/**
 * Где стоят курсоры коллег — то же самое, но «смотрит», а не «правит».
 *
 * Разделено намеренно: правка означает, что значение вот-вот изменится
 * и лезть туда не надо; курсор — просто «человек сейчас здесь». Рисуются
 * они по-разному, иначе таблица покроется одинаковыми рамками и перестанет
 * что-либо сообщать.
 */
const remoteCursors = ref<Record<string, { name: string; color: string }>>({});

function rebuildRemoteEditing(users: PresenceUser[]): void {
  const editing: Record<string, { name: string; color: string }> = {};
  const cursors: Record<string, { name: string; color: string }> = {};

  for (const u of users) {
    if (u.userId === myUserId.value) continue;
    if (u.editing) {
      editing[`${u.editing.rowId}:${u.editing.field}`] = { name: u.name, color: u.color };
    }
    // Курсор показываем только там, где человек ничего не правит:
    // иначе на одной ячейке окажутся обе пометки сразу
    if (u.rowId && u.field && !u.editing) {
      cursors[`${u.rowId}:${u.field}`] = { name: u.name, color: u.color };
    }
  }

  remoteEditing.value = editing;
  remoteCursors.value = cursors;
}

function send(message: ClientMessage): void {
  const currentSocket = socket;
  if (currentSocket?.readyState !== WebSocket.OPEN) return;
  try {
    currentSocket.send(JSON.stringify(message));
  } catch {
    disconnect(currentSocket);
  }
}

/** Закрытие TCP может не прийти при потере сети: тишину тоже считаем обрывом. */
function resetConnectionTimeout(currentSocket: WebSocket): void {
  if (connectionTimeoutTimer !== null) clearTimeout(connectionTimeoutTimer);
  connectionTimeoutTimer = window.setTimeout(() => {
    connectionTimeoutTimer = null;
    disconnect(currentSocket);
  }, HEARTBEAT_INTERVAL_MS * 2);
}

function disconnect(currentSocket: WebSocket): void {
  if (socket !== currentSocket) return;
  // Не ждём onclose: при недоступной сети он тоже может задержаться.
  socket = null;
  cleanupTimers();
  state.value = 'offline';
  currentSocket.close();
  if (started) scheduleReconnect();
}

function connect(): void {
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING))
    return;

  state.value = 'connecting';

  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const currentSocket = new WebSocket(`${proto}//${location.host}${WS_PATH}`);
  socket = currentSocket;
  resetConnectionTimeout(currentSocket);

  socket.onopen = () => {
    if (socket !== currentSocket) return;
    resetConnectionTimeout(currentSocket);
    // Ноль не отправляем: он значит «истории нет», а не «был обрыв».
    // Сервер это тоже проверяет, но незачем спрашивать докат с нуля
    // Подписка обязана попасть на сервер ДО запроса доката.
    send({ type: 'view.subscribe', rowIds: subscribedRows });
    send({ type: 'activity.update', context: activityContext, selection: currentSelection });
    send({
      type: 'client.hello',
      protocolVersion: WS_PROTOCOL_VERSION,
      ...(lastSeq.value > 0 ? { lastSeq: lastSeq.value } : {}),
    });

    if (socket !== currentSocket) return;
    heartbeatTimer = window.setInterval(
      () => send({ type: 'ping', t: Date.now() }),
      HEARTBEAT_INTERVAL_MS,
    );
  };

  socket.onmessage = (evt) => {
    if (socket !== currentSocket) return;
    let msg: ServerMessage;
    try {
      msg = JSON.parse(String(evt.data)) as ServerMessage;
    } catch {
      return;
    }
    if (!msg || typeof msg !== 'object' || typeof msg.type !== 'string') return;
    resetConnectionTimeout(currentSocket);
    if (msg.type === 'server.welcome') {
      state.value = 'online';
      // Сам HTTP upgrade ещё не означает, что приложение обслуживает канал.
      reconnectAttempt = 0;
    }
    handle(msg);
    for (const listener of listeners) listener(msg);
  };

  socket.onclose = (event) => {
    if (socket !== currentSocket) return;
    cleanupTimers();
    socket = null;
    state.value = 'offline';
    if (event.code === 4403 || event.code === 4401) {
      useAuthStore().clearSession();
      useRealtime().stop(true);
      notifyUnauthenticated();
      return;
    }

    /**
     * Закрытие по нашей же команде переподключением не заканчивается.
     *
     * `stop()` снимает флаг и зовёт `socket.close()`, но обработчик закрытия
     * висит на самом сокете и срабатывает уже после — он-то и планировал
     * переподключение. Уход с грида закрывал соединение, а через полсекунды
     * оно поднималось снова: человек на отчётах или в настройках продолжал
     * держать сокет и получать поток правок, а `stop()` выглядел рабочим.
     */
    if (!started) return;

    scheduleReconnect();
  };

  socket.onerror = () => {
    disconnect(currentSocket);
  };
}

function cleanupTimers(): void {
  if (connectionTimeoutTimer !== null) {
    clearTimeout(connectionTimeoutTimer);
    connectionTimeoutTimer = null;
  }
  if (heartbeatTimer !== null) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
}

function scheduleReconnect(): void {
  if (reconnectTimer !== null) return;
  const delay = Math.min(
    RECONNECT_BASE_DELAY_MS * 2 ** reconnectAttempt,
    RECONNECT_MAX_DELAY_MS,
  );
  reconnectAttempt++;
  reconnectTimer = window.setTimeout(() => {
    reconnectTimer = null;
    if (started) connect();
  }, delay);
}

/**
 * Перезагрузка окна данных с задержкой.
 *
 * Пачка массовых операций присылает несколько событий подряд; без
 * склейки каждое запускало бы полный перезапрос грида, и клиент
 * захлебнулся бы там, где хватило одного обновления.
 */
let reloadTimer: number | null = null;
function scheduleReload(grid: ReturnType<typeof useGridStore>): void {
  if (reloadTimer !== null) return;
  reloadTimer = window.setTimeout(() => {
    reloadTimer = null;
    void grid.reload();
  }, 100);
}

// Объекты выбираются из кэша справочников даже вне открытого листа.
// Закрытие объекта или смена адреса в другом окне должны обновить селект.
let dictionaryRefreshTimer: number | null = null;
let dictionaryRefreshPromise: Promise<void> | null = null;
let dictionaryRefreshDirty = false;
let dictionaryRefreshGeneration = 0;
function scheduleDictionaryRefresh(): void {
  dictionaryRefreshDirty = true;
  if (dictionaryRefreshTimer !== null || dictionaryRefreshPromise) return;
  dictionaryRefreshTimer = window.setTimeout(() => {
    dictionaryRefreshTimer = null;
    dictionaryRefreshDirty = false;
    const generation = dictionaryRefreshGeneration;
    const pending = Promise.resolve().then(() => useDictionariesStore().invalidate());
    dictionaryRefreshPromise = pending;
    void pending.catch(() => {
      // При обрыве соединения кэш остаётся читаемым; следующий сигнал
      // изменения или восстановления соединения повторит чтение.
    }).finally(() => {
      if (generation !== dictionaryRefreshGeneration || dictionaryRefreshPromise !== pending) return;
      dictionaryRefreshPromise = null;
      if (dictionaryRefreshDirty) scheduleDictionaryRefresh();
    });
  }, 100);
}

/**
 * Перечитывание открытого листа после чужой правки.
 *
 * Склейка на 100 мс — та же, что у основной таблицы: десять человек,
 * заполняющих лист, дают поток событий, и перечитывать на каждое означало
 * бы держать сеть занятой без толку.
 *
 * Перечитывается ровно тем же запросом, что и всегда: в событии значений
 * нет, и брать их оттуда нечего — права на листах доходят до колонки,
 * а вырезает закрытые только сервер.
 */
let sheetReloadTimer: number | null = null;
let sheetReloadPromise: Promise<void> | null = null;
let sheetReloadGeneration = 0;
const dirtySheets = new Set<string>();
function scheduleSheetReload(sheets: ReturnType<typeof useSheetsStore>): void {
  const code = sheets.code;
  if (!code) return;
  dirtySheets.add(code);
  if (activityContext?.sheetCode !== code || sheetReloadTimer !== null || sheetReloadPromise) return;
  sheetReloadTimer = window.setTimeout(() => {
    sheetReloadTimer = null;
    if (activityContext?.sheetCode !== code || sheets.code !== code) return;
    dirtySheets.delete(code);
    const generation = sheetReloadGeneration;
    let failed = false;
    const pending = Promise.resolve().then(() => sheets.reload());
    sheetReloadPromise = pending;
    const markFailed = () => {
      failed = true;
      if (generation === sheetReloadGeneration) dirtySheets.add(code);
    };
    void pending.then(() => {
      // Стор показывает HTTP-ошибку сам и не пробрасывает reject наружу.
      if (sheets.code === code && sheets.error) markFailed();
    }, markFailed).finally(() => {
      if (generation !== sheetReloadGeneration || sheetReloadPromise !== pending) return;
      sheetReloadPromise = null;
      // События во время запроса сводятся к одному завершающему чтению.
      if (!failed && dirtySheets.has(sheets.code)) scheduleSheetReload(sheets);
    });
  }, 100);
}

function handle(msg: ServerMessage): void {
  const grid = useGridStore();
  const toasts = useToastStore();

  if ('seq' in msg && typeof msg.seq === 'number') lastSeq.value = Math.max(lastSeq.value, msg.seq);

  switch (msg.type) {
    case 'server.welcome':
      myUserId.value = msg.you.userId;
      presence.value = msg.presence;
      rebuildRemoteEditing(msg.presence);
      break;

    case 'presence.state':
      presence.value = msg.presence;
      rebuildRemoteEditing(msg.presence);
      break;

    case 'cell.changed':
      // Другие вкладки того же сотрудника тоже должны получать правки.
      if (isOwnOperation(msg.clientOpId)) break;
      if ((grid.hasDateVisibility || grid.observationActive) && (msg.field === 'sectionId' || msg.field === 'plannedDepartureDate' || msg.derived?.sectionId !== undefined)) {
        scheduleReload(grid);
        break;
      }
      grid.applyRemoteCell(msg.rowId, msg.field, msg.value, msg.derived, msg.version);
      break;

    case 'row.created':
      if (isOwnOperation(msg.clientOpId)) break;
      if (grid.hasDateVisibility || grid.observationActive) { scheduleReload(grid); break; }
      grid.prependRow(msg.row);
      break;

    case 'row.restored':
      scheduleReload(grid);
      break;

    case 'row.deleted':
      grid.removeRow(msg.rowId);
      break;

    case 'row.moved':
      // Применение пар идемпотентно, а другая вкладка автора тоже ждёт перемещения.
      grid.applyRemoteMove(msg.pairs);
      break;

    case 'rows.bulk_changed':
      if (isOwnOperation(msg.clientOpId)) break;
      // Точечно догружать десятки строк дороже, чем перечитать окно.
      //
      // Уведомления при этом нет намеренно: правки коллег — обычный ход
      // работы, а не событие. Раньше на каждую чужую массовую правку
      // выскакивала карточка «Данные обновлены», и человек, который просто
      // заполняет свои строки, получал их весь день. Изменения видны там,
      // где они произошли: строка подсвечивается на секунду.
      scheduleReload(grid);
      break;

    case 'rows.visibility_changed':
      if (!isOwnOperation(msg.clientOpId)) scheduleReload(grid);
      break;

    case 'date_visibility.changed':
      if (msg.userId === null || msg.userId === myUserId.value) scheduleReload(grid);
      break;

    case 'server.reload_required':
      // Уведомление остаётся: таблицу перечитали целиком, и позиция
      // прокрутки с выделением могли сдвинуться — об этом надо сказать,
      // иначе человек решит, что промахнулся сам
      scheduleReload(grid);
      scheduleDictionaryRefresh();
      if (useSheetsStore().code) scheduleSheetReload(useSheetsStore());
      toasts.push({
        kind: 'info',
        title: 'Данные перезагружены',
        message: msg.message,
        dedupeKey: 'reload-required',
      });
      break;

    case 'sheet.changed': {
      if (msg.code === 'work_objects') scheduleDictionaryRefresh();
      const sheets = useSheetsStore();
      // На неактивном листе ничего не загружаем; open() при возврате читает его заново.
      if (sheets.code !== msg.code) break;
      const local = ownSheetOperation(msg.clientOpId, msg.code);
      if (local) {
        const generation = sheetReloadGeneration;
        // Уже начатое чтение могло получить снимок ДО нашей записи и затереть
        // оптимистичный цвет. Его обязательно завершаем актуальным чтением.
        const staleReadPossible = !!sheetReloadPromise || sheets.loading;
        void local.then((applied) => {
          if ((!applied || staleReadPossible) && generation === sheetReloadGeneration && sheets.code === msg.code) {
            scheduleSheetReload(sheets);
          }
        });
      } else scheduleSheetReload(sheets);
      break;
    }

    case 'job.update':
      if (msg.status === 'done') {
        toasts.success('Задача выполнена', msg.message ?? '');
      } else if (msg.status === 'failed') {
        toasts.error('Задача не выполнена', msg.message ?? '');
      }
      break;

    /*
      Администратор поменял общие настройки — предел копирования меняется
      без перезагрузки вкладки. Молча: рекрутёру нечего сообщать, он узнает
      о правиле в тот момент, когда оно ему помешает, и там текст понятнее
      всплывающего «настройки изменены».
    */
    case 'settings.changed':
      // Приходят только изменённые разделы.
      if (msg.security) useAuthStore().applySecurity(msg.security);
      if (msg.appearance) useAppearanceStore().applyRemote(msg.appearance);
      if (msg.cityDefaultColor !== undefined || msg.cityColorsChanged) {
        useDictionariesStore().invalidateCities();
        // В настройках таблица кандидатов не открыта. Её полный reload здесь
        // тянул до 20 000 строк после каждой правки цвета и подвешивал экран.
        // При возврате GridView сам загрузит актуальные цвета из SQL.
        if (activityContext?.path === '/' || activityContext?.path.startsWith('/candidates/')) {
          scheduleReload(grid);
        }
      }
      break;

    case 'server.error':
      toasts.error('Ошибка синхронизации', msg.message);
      break;

    default:
      break;
  }
}

// ── Публичное API ─────────────────────────────────────────

export function useRealtime() {
  const consumer = Symbol('realtime');
  function start(): void {
    consumers.add(consumer);
    if (started) return;
    started = true;
    connect();
  }

  function stop(force = false): void {
    if (force) {
      consumers.clear();
      clearSheetOperations();
    }
    else consumers.delete(consumer);
    if (consumers.size > 0) return;
    started = false;
    reconnectAttempt = 0;
    cleanupTimers();
    if (reconnectTimer !== null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    // Отложенная перезагрузка грида тоже снимается: иначе она сработает
    // уже после ухода со страницы и дёрнет запрос списка без надобности
    if (reloadTimer !== null) {
      clearTimeout(reloadTimer);
      reloadTimer = null;
    }
    if (sheetReloadTimer !== null) clearTimeout(sheetReloadTimer);
    sheetReloadTimer = null;
    sheetReloadGeneration += 1;
    sheetReloadPromise = null;
    dirtySheets.clear();
    if (dictionaryRefreshTimer !== null) clearTimeout(dictionaryRefreshTimer);
    dictionaryRefreshTimer = null;
    dictionaryRefreshPromise = null;
    dictionaryRefreshDirty = false;
    dictionaryRefreshGeneration += 1;
    if (selectionTimer !== null) clearTimeout(selectionTimer);
    selectionTimer = null;
    socket?.close();
    socket = null;
    state.value = 'offline';

    /*
      Список присутствующих чистится вместе с соединением.

      Пока он оставался, кружки коллег висели в шапке и на отчётах, и на
      сотрудниках — там, где сокет закрыт и обновлять их некому. Подсказка
      при этом уверенно сообщала «правит ячейку» про человека, который
      вышел полчаса назад. Показывать неправду о людях хуже, чем не
      показывать ничего.
    */
    presence.value = [];
    remoteEditing.value = {};
    remoteCursors.value = {};
    subscribedRows = [];
    currentSelection = null;
    selectionKey = '';
    activityContext = undefined;
    lastSeq.value = 0;
    myUserId.value = null;
  }

  /** Сообщить серверу, какие строки видны — по остальным событий не будет. */
  function subscribeRows(rowIds: string[]): void {
    if (rowIds.length === subscribedRows.length && rowIds.every((id, i) => id === subscribedRows[i])) return;
    subscribedRows = [...rowIds];
    send({ type: 'view.subscribe', rowIds: subscribedRows });
  }

  function setActivityContext(context: ActivityContext): void {
    if (activityContext?.path !== context.path) {
      currentSelection = null;
      selectionKey = '';
    }
    activityContext = context;
    if (sheetReloadTimer !== null) clearTimeout(sheetReloadTimer);
    sheetReloadTimer = null;
    const sheets = useSheetsStore();
    if (context.sheetCode === sheets.code && dirtySheets.has(sheets.code)) scheduleSheetReload(sheets);
    send({ type: 'activity.update', context, selection: currentSelection });
  }

  type SelectionInput = Omit<ActivitySelection, 'rowCount' | 'cellCount'> & { rowCount?: number; cellCount?: number };
  function normalizedSelection(input: SelectionInput): ActivitySelection {
    const rowCount = input.rowCount ?? input.rowIds.length;
    return { ...input, rowIds: input.rowIds.slice(0, 100), rowCount, cellCount: input.cellCount ?? rowCount * input.fields.length };
  }

  function reportSelection(input: SelectionInput): void {
    const selection = normalizedSelection(input);
    const key = JSON.stringify(selection);
    if (key === selectionKey) return;
    selectionKey = key;
    currentSelection = selection.rowCount > 0 ? selection : null;
    // Дребезг протягивания мышью сводится к одному законченному диапазону.
    if (selectionTimer !== null) clearTimeout(selectionTimer);
    selectionTimer = window.setTimeout(() => {
      selectionTimer = null;
      send({ type: 'activity.update', selection: currentSelection });
      if (selection.rowCount > 1 || selection.fields.length > 1) {
        void api.post('/users/activity', { ...selection, kind: 'selection' }).catch(() => {});
      }
    }, 300);
  }

  async function reportCopy(input: SelectionInput & { outcome: 'attempted' | 'blocked' }): Promise<void> {
    try {
      await api.post('/users/activity', { ...normalizedSelection(input), kind: 'copy', outcome: input.outcome });
    } catch {
      useToastStore().push({ kind: 'error', title: 'Не удалось записать попытку копирования', dedupeKey: 'copy-audit-failed' });
    }
  }

  function onMessage(listener: (message: ServerMessage) => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function moveCursor(rowId: string | null, field: string | null): void {
    send({ type: 'cursor.move', rowId, field });
  }

  function beginEditing(rowId: string, field: string): void {
    send({ type: 'cell.edit.begin', rowId, field });
  }

  function endEditing(rowId: string, field: string): void {
    send({ type: 'cell.edit.end', rowId, field });
  }

  /** Кто правит эту ячейку прямо сейчас (не считая нас самих). */
  function editingCell(rowId: string, field: string): { name: string; color: string } | null {
    return remoteEditing.value[`${rowId}:${field}`] ?? null;
  }

  const others = computed(() => presence.value.filter((p) => p.userId !== myUserId.value));

  return {
    state: computed(() => state.value),
    isOnline: computed(() => state.value === 'online'),
    presence: computed(() => presence.value),
    others,
    start,
    stop,
    subscribeRows,
    setActivityContext,
    reportSelection,
    reportCopy,
    onMessage,
    moveCursor,
    remoteCursors,
    beginEditing,
    endEditing,
    editingCell,
    sendObservation: (message: ClientObservationMessage) => send(message),
  };
}
